import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import {
  Shield,
  HardHat,
  FolderKanban,
  ListChecks,
  DollarSign,
  Loader2,
  Check,
  Boxes,
  AlertCircle,
  Info,
} from 'lucide-react';
import { useSelectModules, useSkipOnboarding } from '@/hooks/use-onboarding';

// Module metadata mirroring backend/core/modules.py
const MODULE_INFO: Record<
  string,
  {
    name: string;
    description: string;
    icon: React.ReactNode;
    recommends: string[];
  }
> = {
  safety: {
    name: 'H3Safe',
    description: 'Safety compliance — JSA/FLHA, toolbox talks, near-miss reporting',
    icon: <Shield className="h-6 w-6" />,
    recommends: [],
  },
  crew: {
    name: 'H3Crew',
    description: 'Workforce management — tech profiles, certifications, scheduling',
    icon: <HardHat className="h-6 w-6" />,
    recommends: [],
  },
  projects: {
    name: 'H3Books',
    description: 'Billing & project finances — estimates, bids, invoicing, change orders',
    icon: <FolderKanban className="h-6 w-6" />,
    recommends: ['crew'],
  },
  work: {
    name: 'H3Project',
    description: 'Work management — tasks/checklists under foundation Projects',
    icon: <ListChecks className="h-6 w-6" />,
    recommends: ['projects'],
  },
  payroll: {
    name: 'H3Pay',
    description: 'Payroll & expenses — timesheets, expense tracking, payroll export',
    icon: <DollarSign className="h-6 w-6" />,
    recommends: ['crew', 'projects'],
  },
};

const REC_LABELS: Record<string, string> = {
  crew: 'H3Crew',
  projects: 'H3Books',
};

interface ModulePickerStepProps {
  availableModules: string[];
  selectedModules?: string[];
  onComplete: () => void;
}

export function ModulePickerStep({
  availableModules,
  selectedModules,
  onComplete,
}: ModulePickerStepProps) {
  const [selected, setSelected] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (selectedModules) {
      for (const key of selectedModules) {
        if (MODULE_INFO[key]) {
          initial.add(key);
        }
      }
    }
    return initial;
  });
  const selectModules = useSelectModules();
  const skipOnboarding = useSkipOnboarding();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const toggleModule = (key: string) => {
    setError(null);
    const next = new Set(selected);

    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }

    setSelected(next);
  };

  const getRecommendationHint = (key: string): string | null => {
    const info = MODULE_INFO[key];
    if (!info || info.recommends.length === 0) return null;
    const missing = info.recommends.filter((dep) => !selected.has(dep));
    if (missing.length === 0) return null;
    const names = missing.map((k) => REC_LABELS[k] || k);
    return `Works better with ${names.join(' and ')}`;
  };

  const handleConfirm = () => {
    if (selected.size === 0) {
      setError('Please select at least one module.');
      return;
    }

    selectModules.mutate(
      { module_keys: Array.from(selected) },
      {
        onSuccess: () => onComplete(),
        onError: (err) => {
          const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            'Failed to save module selection. Please try again.';
          setError(msg);
        },
      }
    );
  };

  const handleFinishLater = async () => {
    setError(null);
    try {
      await selectModules.mutateAsync({ module_keys: Array.from(selected) });
      await skipOnboarding.mutateAsync();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to save setup. Please try again.';
      setError(msg);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:py-12">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <Boxes className="h-8 w-8 text-blue-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
          Choose your modules
        </h1>
        <p className="mt-2 text-gray-500">
          Your plan includes H3Gear (inventory) by default. Select the additional
          modules you&apos;d like to enable for your organization.
        </p>
      </div>

      {/* Always-included H3Gear card */}
      <Card className="mb-4 border-green-200 bg-green-50/50">
        <CardContent className="flex items-center gap-4 py-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100">
            <Check className="h-6 w-6 text-green-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-green-900">H3Gear</h3>
            <p className="text-sm text-green-700">
              Asset tracking, inspections, deployments &amp; kits — always included
            </p>
          </div>
          <Badge className="bg-green-600 text-white">Included</Badge>
        </CardContent>
      </Card>

      {/* Selectable module cards */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Available modules</CardTitle>
          <CardDescription>
            Click a card to toggle it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {availableModules
            .filter((key) => MODULE_INFO[key])
            .map((key) => {
              const info = MODULE_INFO[key];
              const isSelected = selected.has(key);
              const recHint = getRecommendationHint(key);

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleModule(key)}
                  className={`flex w-full items-start gap-4 rounded-lg border p-4 text-left transition-colors ${
                    isSelected
                      ? 'border-blue-300 bg-blue-50/50 ring-1 ring-blue-200'
                      : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/30'
                  }`}
                >
                  <div
                    className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg ${
                      isSelected
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {info.icon}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3
                        className={`font-medium ${
                          isSelected ? 'text-blue-900' : 'text-gray-900'
                        }`}
                      >
                        {info.name}
                      </h3>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">{info.description}</p>
                    {recHint && isSelected && (
                      <div className="flex items-center gap-1 mt-1.5">
                        <Info className="h-3 w-3 text-blue-500" />
                        <span className="text-xs text-blue-600">{recHint}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex-shrink-0 mt-1">
                    <div
                      className={`flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors ${
                        isSelected
                          ? 'border-blue-600 bg-blue-600'
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      {isSelected && <Check className="h-4 w-4 text-white" />}
                    </div>
                  </div>
                </button>
              );
            })}
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
        <Button
          variant="ghost"
          onClick={() => void handleFinishLater()}
          disabled={selectModules.isPending || skipOnboarding.isPending}
        >
          {(selectModules.isPending || skipOnboarding.isPending) && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          I&apos;ll finish this later
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={selected.size === 0 || selectModules.isPending || skipOnboarding.isPending}
          className="min-w-[160px]"
        >
          {selectModules.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          {selected.size === 0
            ? 'Select modules to continue'
            : `Continue with ${selected.size} module${selected.size > 1 ? 's' : ''}`}
        </Button>
      </div>
    </div>
  );
}
